import React from 'react';
import Navigation from '../components/Navigation';
import TutorialHint from '../components/TutorialHint';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Youtube, Radio, BookOpen, Mic } from 'lucide-react';

const Livestream = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8 animate-fade-in text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center mx-auto mb-4">
            <Youtube className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#2B6B7F] mb-3">
            Live Media
          </h1>
          <div className="flex items-center justify-center gap-3 mb-2">
            <TutorialHint videoId="Juosmo_R8oQ" />
            <p className="text-slate-600">Stream worship, listen to podcasts, and enjoy Christian radio</p>
          </div>
        </div>

        {/* Tabs for 4 Media Components */}
        <Tabs defaultValue="livestream" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-auto gap-1">
            <TabsTrigger value="livestream" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center">
              <Youtube className="w-5 h-5 flex-shrink-0 text-red-500" />
              <span className="text-xs sm:text-sm leading-tight">Live<br/>Stream</span>
            </TabsTrigger>
            <TabsTrigger value="podcast" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center">
              <Mic className="w-5 h-5 flex-shrink-0 text-green-500" />
              <span className="text-xs sm:text-sm leading-tight">WFMC<br/>Podcast</span>
            </TabsTrigger>
            <TabsTrigger value="radio" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center">
              <Radio className="w-5 h-5 flex-shrink-0 text-purple-500" />
              <span className="text-xs sm:text-sm leading-tight">Christian<br/>Radio</span>
            </TabsTrigger>
            <TabsTrigger value="audiobible" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center">
              <BookOpen className="w-5 h-5 flex-shrink-0 text-blue-500" />
              <span className="text-xs sm:text-sm leading-tight">Audio<br/>Bible</span>
            </TabsTrigger>
          </TabsList>

          {/* 1. YouTube Stream Tab */}
          <TabsContent value="livestream">
            <Card className="border-t-4 border-t-red-500">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Westland FMC Live Stream
                </CardTitle>
                <CardDescription>
                  Watch our live worship service every Sunday morning
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="aspect-video w-full bg-slate-900 rounded-lg overflow-hidden">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://www.youtube.com/embed/live_stream?channel=UC2VHF99xtyw9W2-sMVEL_rQ" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    title="Westland FMC Live Stream"
                    className="w-full h-full"
                  />
                </div>
                
                <div className="mt-6 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-teal-50 border border-blue-200">
                  <h3 className="font-semibold text-slate-800 mb-2">Service Times</h3>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Join us live every Sunday morning for worship, teaching, and fellowship. Can't make it live? 
                    Past services are available on our YouTube channel.
                  </p>
                  <div className="mt-4">
                    <a
                      href="https://www.westlandfmc.com/live/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#2B6B7F] hover:text-[#1E4D5F] font-medium text-sm"
                    >
                      <Youtube className="w-4 h-4" />
                      Visit our Live page for more info
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* 2. Podcast Tab */}
          <TabsContent value="podcast">
            <Card className="border-t-4 border-t-green-500">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  WFMC Podcast
                </CardTitle>
                <CardDescription>
                  Listen to our latest podcast episodes on Spotify
                </CardDescription>
              </CardHeader>
              <CardContent>
                <iframe 
                  data-testid="embed-iframe" 
                  style={{ borderRadius: '12px' }}
                  src="https://open.spotify.com/embed/show/1VmrQVLbwK4eskSPNKsi0C?utm_source=generator" 
                  width="100%" 
                  height="352" 
                  frameBorder="0" 
                  allowFullScreen 
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                  loading="lazy"
                  title="Westland FMC Podcast"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* 3. Christian Radio Tab */}
          <TabsContent value="radio">
            <Card className="border-t-4 border-t-purple-500">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Christian Radio Live
                </CardTitle>
                <CardDescription>
                  Listen to uplifting Christian music and teaching 24/7
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full bg-slate-900 rounded-lg overflow-hidden relative">
                  <iframe 
                    width="100%" 
                    height="520"
                    src="https://myflr.org/stream/?autoplay=1" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" 
                    allowFullScreen
                    title="Christian Radio Live"
                    className="w-full"
                    loading="eager"
                  />
                </div>
                <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">
                    If the player doesn't load, click below to open in a popup:
                  </p>
                  <button
                    onClick={() => window.open('https://myflr.org/stream/', 'RadioPlayer', 'width=400,height=600,scrollbars=no,resizable=yes')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    <Radio className="w-4 h-4" />
                    Open Radio Player
                  </button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* 4. Audio Bible Tab */}
          <TabsContent value="audiobible">
            <Card className="border-t-4 border-t-blue-500">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Audio Bible
                </CardTitle>
                <CardDescription>
                  Listen to the Bible read aloud with dramatic audio
                </CardDescription>
              </CardHeader>
              <CardContent>
                <iframe 
                  src="https://live.bible.is/bible/engesv/mat/1?audio_type=audio_drama"
                  title="Audio Bible"
                  className="w-full border-0 rounded-lg h-[85vh] min-h-[560px] max-h-[900px]"
                  allow="autoplay"
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Livestream;
